import { useContext } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import { useTranslate } from '@/hooks/useTranslate';
import { useApplePayTabUnusable } from '@/hooks/useApplePayTabUnusable';
import { isMultiOfferCart } from '@/utils/offers';
import ErrorIcon from '@/assets/icons/common/error.svg';
import { OffersPane } from '../OffersPane/OffersPane';
import { ProfileCircle } from '../PlayerDetails/ProfileCircle';
import { PurchasedItems } from '../PurchasedItems/PurchasedItems';
import { PriceDetails } from '../PriceDetails/PriceDetails';
import { TotalPrice } from '../PriceDetails/TotalPrice';
import { Divider } from '../Common/Divider';

/**
 * The order summary inside the Apple Pay fallback tab (Figma).
 *
 * That tab exists for one reason — Apple's sheet needs a top-level window — so
 * it reads as a plain review screen, not as the store: publisher header, the
 * "Complete Your Purchase" title, the product rows fenced by two hairlines,
 * then the breakdown. Nothing collapses and there is no Details toggle:
 * reviewing the order IS the screen, so the layout the checkout uses (a
 * collapsible under a hero band) doesn't apply here.
 */
export const ApplePayTabDetails = () => {
  const { boot, offers, pricing, purchasedItems } = useContext(CheckoutContext);
  const { t } = useTranslate();
  const isWalletUnusable = useApplePayTabUnusable();
  // The Builder game-name override drives only the native Apple Pay sheet label
  // (ACDEV-29232), not this review screen's publisher header (ACDEV-29510).
  const publisherName = boot?.checkoutTheme?.publisherName ?? '';
  // A multi-offer cart is reviewed as offers everywhere else (OffersPane in
  // PlayerDetails), and `purchasedItems` is the flat product list underneath
  // them — showing it here would review a different cart than the one the
  // shopper built. The pane brings its own boxed surface, so it replaces the
  // hairline-fenced list rather than sitting inside it.
  const isMultiOfferCartCheckout = isMultiOfferCart(offers);

  return (
    <div data-testid="apple-pay-tab-details" className="flex w-full flex-col">
      {/* `pr-8` keeps the name clear of the absolutely-positioned XButton. */}
      <div className="flex flex-row items-center gap-3 min-w-0 pr-8 font-semibold">
        <ProfileCircle />
        <div className="truncate min-w-0" title={publisherName}>
          {publisherName}
        </div>
      </div>
      <div className="mt-4 font-semibold">{t('completeYourPurchase')}</div>
      {isMultiOfferCartCheckout ? (
        <div className="mt-3">
          <OffersPane lineItems={offers} />
        </div>
      ) : (
        purchasedItems.length > 0 && (
          <div className="flex flex-col gap-4 mt-3">
            <Divider />
            {/* `bare`: no gray box behind the list, no tile behind each asset —
                clean rows on white, as in the mock. */}
            <PurchasedItems bare />
            <Divider />
          </div>
        )
      )}
      <div className="flex flex-col gap-2 mt-3">
        <PriceDetails />
        <TotalPrice pricing={pricing} />
      </div>
      {isWalletUnusable && <ApplePayTabUnavailableNotice />}
    </div>
  );
};

/**
 * Terminal state for this tab: the wallet it was opened for reported that it
 * cannot run here either. The tab has no second act — it carries no other
 * payment method and cannot open a further tab — so the only honest thing on
 * screen is why the button never came, plus the one move left. Closing the
 * tab (the X above, or the browser's own close) returns the shopper to the
 * framed checkout, where the other methods live.
 */
const ApplePayTabUnavailableNotice = () => {
  const { t } = useTranslate();

  return (
    <div
      data-testid="apple-pay-tab-unavailable"
      role="alert"
      className="mt-4 flex items-start gap-3 rounded-lg border border-base-border px-4 py-3"
    >
      <img src={ErrorIcon} className="w-5 h-5 shrink-0 mt-0.5" alt="" />
      {/* `whitespace-pre-line`: the copy is two sentences on two lines. */}
      <div className="text-sm text-base-foreground leading-snug whitespace-pre-line text-left">
        {t('paymentMethodUnavailableCloseTab')}
      </div>
    </div>
  );
};
